import { CssBaseline } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useRoutes } from 'react-router';

import CommonLoader from '@/components/CommonLoader';
import LogoutSuccessModal from '@/components/LogoutSuccessModal';
import SessionTimeoutModal from '@/components/SessionTimeoutModal';
import { useAuth } from '@/contexts/AuthContext';
import { StatusUnavailable } from '@/pages/public/Status/Unavailable';
import { publicRoutes } from '@/router/public-routes';
import ThemeProviderWrapper from '@/theme/ThemeProvider';
import {
  DASHBOARD_URL,
  TENANT_ADMINISTRATION_HUB_URL,
  TENANT_MY_PERSONAL_LEDGER_URL,
} from '@/utils/constants/paths';

export default function App() {
  const location = useLocation();

  const navigate = useNavigate();

  const {
    role,
    isLoading,
    isAuthenticated,
    logout,
    sessionTimeout,
    logoutSuccess,
    isLoginLoading,
    tenantInfo,
    isTenantInactive,
    isTenantNotFound,
  } = useAuth();
  const [routes, setRoutes] = useState(publicRoutes);
  const [routesLoaded, setRoutesLoaded] = useState(true);
  const redirectedForMakerChecker = useRef(false);
  useEffect(() => {
    const loadRoutes = async () => {
      if (!role) {
        setRoutes(publicRoutes);
        setRoutesLoaded(true);
        return;
      }

      setRoutesLoaded(false);

      try {
        if (role === 'admin') {
          const adminModule = await import('./router/admin-routes');
          setRoutes(adminModule.default);
        } else if (role === 'user') {
          const tenantModule = await import('./router/tenant-routes');
          setRoutes(tenantModule.default);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading routes:', error);
        setRoutes(publicRoutes);
      } finally {
        setRoutesLoaded(true);
      }
    };
    loadRoutes();
  }, [role]);

  useEffect(() => {
    const faviconUrl = tenantInfo?.faviconFilePath || '/favicon.ico';
    const links = document.querySelectorAll("link[rel*='icon']");
    if (links.length > 0) {
      links.forEach(link => {
        (link as HTMLLinkElement).href = faviconUrl;
      });
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconUrl;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [tenantInfo]);

  useEffect(() => {
    if (!isAuthenticated) {
      redirectedForMakerChecker.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (
      isAuthenticated &&
      role &&
      tenantInfo &&
      !redirectedForMakerChecker.current
    ) {
      if (
        tenantInfo?.tenantIdentifier !== 'admin' &&
        !tenantInfo?.makerCheckerPrinciple
      ) {
        redirectedForMakerChecker.current = true;
        // The Administration Hub is not this tenant's page. It used to also
        // catch `/` and `/dashboard`, but those are the Personal Dashboard
        // now, so only the hub's own path is redirected away from.
        if (location.pathname === TENANT_ADMINISTRATION_HUB_URL) {
          navigate(TENANT_MY_PERSONAL_LEDGER_URL, { replace: true });
        }
      }
    }
  }, [isAuthenticated, role, tenantInfo, navigate, location.pathname]);

  useEffect(() => {
    if (isAuthenticated && role && location.pathname === '/') {
      if (
        tenantInfo?.tenantIdentifier !== 'admin' &&
        !tenantInfo?.makerCheckerPrinciple
      ) {
        navigate(DASHBOARD_URL, { replace: true });
      }
    } else if (!isAuthenticated && role) {
      setRoutes(publicRoutes);
    }
  }, [isAuthenticated, role, location.pathname, navigate, tenantInfo]);

  useEffect(() => {
    if (
      (isTenantInactive || isTenantNotFound) &&
      location.pathname !== '/site-unavailable'
    ) {
      navigate('/site-unavailable', { replace: true });
    }
  }, [isTenantInactive, isTenantNotFound, location.pathname, navigate]);

  const content = useRoutes(routes);

  if (isLoading || !routesLoaded) {
    return <CommonLoader isLoading={true} />;
  }

  if (isTenantInactive || isTenantNotFound) {
    return (
      <ThemeProviderWrapper>
        <CssBaseline />
        <StatusUnavailable />
      </ThemeProviderWrapper>
    );
  }

  if (role && !isAuthenticated) return null;

  return (
    <ThemeProviderWrapper>
      <CssBaseline />
      {content}
      {sessionTimeout.isOpen && (
        <SessionTimeoutModal
          open={sessionTimeout.isOpen}
          remainingSeconds={sessionTimeout.remainingSeconds}
          onLogout={logout}
          onStayLoggedIn={sessionTimeout.onStayLoggedIn}
          onLogin={sessionTimeout.onLogin}
          isLoading={isLoginLoading}
        />
      )}
      {logoutSuccess.isOpen && (
        <LogoutSuccessModal
          open={logoutSuccess.isOpen}
          sessionDuration={logoutSuccess.sessionDuration}
          remainingSeconds={logoutSuccess.remainingSeconds}
          onClose={logoutSuccess.onClose}
          isLoading={isLoginLoading}
        />
      )}
    </ThemeProviderWrapper>
  );
}
